<template>
  <v-row class="form-step">
    <v-col>
      <label class="label"
        >Select Sectors
        <v-tooltip text="Zero or any number of sectors can be selected.">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              size="x-small"
              icon
              variant="outlined"
              color="#0d47a1"
            >
              <v-icon icon="fas fa-info" />
            </v-btn>
          </template>
        </v-tooltip>
      </label>
      <v-row>
        <v-col cols="4">
          <v-select
            v-model="selectedSector"
            label="Select Sector"
            :items="optionsSectors"
            item-title="text"
            item-value="value"
            multiple
            chips
            variant="solo"
            hint="Select one of the available sectors"
            persistent-hint
            class="mb-6"
            @update:modelValue="sectorLevelHandler"
            :rules="sectorPresenceRule"
          ></v-select>
        </v-col>
      </v-row>
      <v-form fast-fail>
        <v-row>
          <v-col cols="12" v-if="form.kpi.sectors_attributes.length > 0">
            <div style="margin-top: 15px">
              <label class="label"
                >Enter a weight for each sector and aggregation level pair
                <v-tooltip
                  text="Enter values in range [0,1]. Non-normalized weights will be supported in the future."
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      size="x-small"
                      icon
                      variant="outlined"
                      color="#0d47a1"
                    >
                      <v-icon icon="fas fa-info" />
                    </v-btn>
                  </template>
                </v-tooltip>
              </label>
            </div>
          </v-col>
          <v-col
            cols="12"
            v-for="(sector, index) in selectedSector"
            :key="sector"
          >
            <hr class="mb-6" />
            <span
              ><strong>{{
                StringManipulation.methods.clearAndCapitalize(sector)
              }}</strong></span
            >
            <!-- <v-checkbox
              :id="`${sector}_equal_w`"
              v-model="form.kpi.sectors_attributes[index].equal_weights"
              :name="`${sector}_equal_w`"
              true-value="true"
              false-value="false"
              class="ml-7"
              >Equal weights
            </v-checkbox> -->
            <v-row class="mt-2 mb-2">
              <v-col
                cols="3"
                v-if="
                  sector &&
                  (sector.equal_weights === 'false' ||
                    sector.equal_weights === null ||
                    sector.equal_weights === undefined)
                "
                v-for="(level, index) in form.aggregationLevels"
                :key="`${sector}_${level.name}`"
              >
                <span>
                  <strong>{{ level.name }}</strong>
                  <v-text-field
                    :id="`${sector}_${level.name}_dimension_weight_`"
                    v-model="form.aggregationLevels[index][`sector_${sector}_weight`]"
                    type="text"
                    :placeholder="getPlaceholder(index, `sector_${sector}_weight`)"
                    :readonly="
                      form.aggregationLevels[index][
                        `sector_${sector}_weight`
                      ] === null
                    "
                    variant="solo"
                    :bg-color="fieldColorCoding(level.name)"
                    required
                    :rules=weightRule
                  >
                  </v-text-field>
                  <span
                    v-if="
                      accumulatedWeights[sector] &&
                      parseFloat(accumulatedWeights[sector][level.name]) >
                        0
                    "
                  >
                    <i class="text-amber-darken-3">
                      (accumulated weight: <b>{{
                        accumulatedWeights[sector][level.name]
                      }}</b>)
                    </i>
                  </span>
                  <i class="text-blue-grey-darken-2" v-else>(No accumulated weight)</i>
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script setup>
import StringManipulation from "@/Mixins/StringManipulation";
import { fieldColorCoding } from "@/Shared/StepsRules.vue";
import { weightRule, sectorPresenceRule } from "@/Shared/StepsRules.vue";
import { computed, onMounted, ref } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  accumulatedWeights: {
    type: Object,
    required: false,
    default: null,
  },
  ignoredWeightsServer: {
    type: Array,
    required: false,
    default: function () {
      return [];
    },
  },
});

let form = props.modelValue;

const selectedSector = ref([])

const optionsSector = [
  "climate_change_&_mitigation",
  "quality_of_life_&_prosperity",
  "resource_efficiency",
  "smart_&_reliable_infrastructure",
];
const optionsSectors = [
  { value: "climate_change_&_mitigation", text: "Climate Change & Mitigation" },
  {
    value: "quality_of_life_&_prosperity",
    text: "Quality of Life & Prosperity",
  },
  { value: "resource_efficiency", text: "Resource Efficiency" },
  {
    value: "smart_&_reliable_infrastructure",
    text: "Smart & Reliable Infrastructure",
  },
];
const alreadySelectedSector = ref(false);
// a model for the check boxes of ignored buttons
// because if there is not one then it starts unchecked and there is no way
// to start it checked if the weights are ignored (edit page mainly)
const ignoredWeights = ref({});
const ignoredWeightsReady = ref(false);

/**
 * Callback that is activated after the selected sectors change.
 * Checks if a sector was added or removed and calls the corresponding
 * methods to handle the type of event.
 * 
 * @param {Array} level - Contains the values of the selected sectors.
 */
function sectorLevelHandler(selectedSectors) {
  let formSectors = form.kpi.sectors_attributes.map(e => e.name);
  if (selectedSectors.length > formSectors.length) {
    // It is certain that the two arrays will differ by exactly one element,
    // since the callback is triggered everytime a new option is selected.
    const addedSector = selectedSectors.filter(s => !formSectors.includes(s))[0]
    addSector(addedSector);
  } else {
    const removedSector = formSectors.filter(s => !selectedSectors.includes(s))[0]
    removeSector(removedSector);
  }
}

/**
 * Adds a new sector to the KPI form data.
 * 
 * @param {String} sector - The name of the sector to be added in the form data.
 */
function addSector(sector) {
  const kpiFormSectors = form.kpi.sectors_attributes
  kpiFormSectors.push({ name: sector, equal_weights: "false" });
}

function removeSector(sector) {
  const kpiFormSectors = form.kpi.sectors_attributes
  const index = kpiFormSectors.findIndex((prop) => prop.name === sector);
  kpiFormSectors.splice(index, 1);
}

function callOnChange(index, sector, value) {
  if (value === "ignore")
    this.$set(form.aggregationLevels[index], sector, null);
  else this.$set(form.aggregationLevels[index], sector, "");
}

function getPlaceholder(index, sector) {
  if (form.aggregationLevels[index][sector] === null)
    return "Weight is ignored";
  return "Enter weight";
}

function ignoreWeight(dimension, level) {
  return (
    props.ignoredWeightsServer.filter(
      (e) =>
        e.dimension.toLowerCase() === dimension &&
        e.level === level.toLowerCase()
    ).length > 0
  );
}

onMounted(() => {
  // set the model for checkboxes of ignored weights
  const levels = form.aggregationLevels;
  const checkboxes = ignoredWeights.value;
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    const levelName = level.name;
    const keys = Object.keys(level);

    for (let k = 0; k < keys.length; k++) {
      const key = keys[k];

      if (key.includes("sector_")) {
        const weight = level[key];
        let isIgnored = "specify";
        if (weight === null) isIgnored = "ignore";
        const cleanKey = key.replace("sector_", "").replace("_weight", "");
        let hash = {};
        if (checkboxes[cleanKey]) hash = checkboxes[cleanKey];
        hash[levelName] = isIgnored;
      }
    }
  }
  ignoredWeightsReady.value = true;

  // find if there are null but something has changed in ignore weights
  // e.g. saved a kpi when mobility-building was being ignored
  // but the user changes this preference so it has to be '' from null
  const aggLevels = form.aggregationLevels;
  for (let i = 0; i < aggLevels.length; i++) {
    const keys = Object.keys(aggLevels[i]).filter((e) => e.includes("sector_"));
    for (let j = 0; j < keys.length; j++) {
      const sKey = keys[j];
      if (aggLevels[i][sKey] === null) {
        const sectorName = sKey.replace("sector_", "").replace("_weight", "");
        const levelName = sKey["name"];
        const item = props.ignoredWeightsServer.filter(
          (e) => e.dimension === sectorName && e.level === levelName
        )[0];
        if (!item) aggLevels[i][sKey] = "";
      }
    }
  }

  // ---- new from server ignored weights
  for (let i = 0; i < props.ignoredWeightsServer.length; i++) {
    const item = props.ignoredWeightsServer[i];
    const alItem = form.aggregationLevels.filter(
      (e) => e.name === item.level
    )[0];
    if (alItem) {
      alItem[`sector_${item.dimension}_weight`] = null;
    }

    if (ignoredWeights.value[item.dimension] !== undefined)
      ignoredWeights.value[item.dimension][item.level] = "ignore";
  }

  // -- set selected sector
  selectedSector.value = form.kpi.sectors_attributes.map(e => e.name)
});
</script>

<style scoped>
strong {
  font-size: medium;
}
.label {
  font-size: medium;
}
</style>
